import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Separator } from "@/components/ui/separator";
import { Plus, Search, Trash2, Edit, Users, UserPlus, Mail, Phone, Star, ChevronDown, ChevronUp } from "lucide-react";
import { toast } from "sonner";

export default function ClientsPage() {
  const { apiFetch, API } = useAuth();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editClient, setEditClient] = useState(null);
  const [expandedClient, setExpandedClient] = useState(null);
  const [contactDialog, setContactDialog] = useState(false);
  const [contactForm, setContactForm] = useState({ name: "", email: "", phone: "", position: "", is_primary: false });
  const [contactClientId, setContactClientId] = useState(null);

  const [form, setForm] = useState({
    company_name: "", contact_name: "", email: "", phone: "",
    address: "", city: "", postal_code: "", siret: "", notes: ""
  });

  const fetchClients = useCallback(async () => {
    const res = await apiFetch(`${API}/clients`);
    if (res.ok) setClients(await res.json());
    setLoading(false);
  }, [apiFetch, API]);

  useEffect(() => { fetchClients(); }, [fetchClients]);

  const openNew = () => {
    setEditClient(null);
    setForm({ company_name: "", contact_name: "", email: "", phone: "", address: "", city: "", postal_code: "", siret: "", notes: "" });
    setDialogOpen(true);
  };

  const openEdit = (c) => {
    setEditClient(c);
    setForm({
      company_name: c.company_name || "", contact_name: c.contact_name || "", email: c.email || "",
      phone: c.phone || "", address: c.address || "", city: c.city || "",
      postal_code: c.postal_code || "", siret: c.siret || "", notes: c.notes || ""
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.company_name) { toast.error("Le nom de la société est requis"); return; }
    let res;
    if (editClient) {
      res = await apiFetch(`${API}/clients/${editClient.client_id}`, { method: "PUT", body: JSON.stringify(form) });
    } else {
      res = await apiFetch(`${API}/clients`, { method: "POST", body: JSON.stringify(form) });
    }
    if (res.ok) {
      toast.success(editClient ? "Client mis à jour" : "Client créé");
      setDialogOpen(false);
      fetchClients();
    } else {
      const err = await res.json();
      toast.error(err.detail || "Erreur");
    }
  };

  const handleDelete = async (clientId) => {
    if (!window.confirm("Supprimer ce client ?")) return;
    const res = await apiFetch(`${API}/clients/${clientId}`, { method: "DELETE" });
    if (res.ok) { toast.success("Client supprimé"); fetchClients(); }
  };

  const openAddContact = (clientId) => {
    setContactClientId(clientId);
    setContactForm({ name: "", email: "", phone: "", position: "", is_primary: false });
    setContactDialog(true);
  };

  const handleAddContact = async () => {
    if (!contactForm.name || !contactForm.email) { toast.error("Nom et email requis"); return; }
    const res = await apiFetch(`${API}/clients/${contactClientId}/contacts`, { method: "POST", body: JSON.stringify(contactForm) });
    if (res.ok) { toast.success("Contact ajouté"); setContactDialog(false); fetchClients(); }
    else { const err = await res.json(); toast.error(err.detail || "Erreur"); }
  };

  const handleDeleteContact = async (clientId, contactId) => {
    if (!window.confirm("Supprimer ce contact ?")) return;
    const res = await apiFetch(`${API}/clients/${clientId}/contacts/${contactId}`, { method: "DELETE" });
    if (res.ok) { toast.success("Contact supprimé"); fetchClients(); }
  };

  const filtered = clients.filter(c =>
    (c.company_name || "").toLowerCase().includes(search.toLowerCase()) ||
    (c.contact_name || "").toLowerCase().includes(search.toLowerCase()) ||
    (c.email || "").toLowerCase().includes(search.toLowerCase())
  );

  if (loading) return <div className="p-4 sm:p-8"><div className="animate-pulse h-96 bg-slate-200 rounded-lg" /></div>;

  return (
    <div className="p-4 sm:p-8" data-testid="clients-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6 gap-3">
        <div>
          <h1 className="font-barlow text-xl sm:text-2xl font-bold text-slate-900 tracking-tight uppercase">Clients</h1>
          <p className="text-sm text-slate-500 mt-1">{clients.length} entreprise(s)</p>
        </div>
        <Button onClick={openNew} className="bg-slate-900 hover:bg-slate-800 gap-2" data-testid="new-client-btn">
          <Plus className="w-4 h-4" /> Nouveau Client
        </Button>
      </div>

      {/* Search */}
      <div className="relative mb-4">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
        <Input placeholder="Rechercher un client..." value={search} onChange={e => setSearch(e.target.value)} className="pl-9" data-testid="search-clients" />
      </div>

      <Card className="border-slate-200">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="text-xs font-semibold">Société</TableHead>
                  <TableHead className="text-xs font-semibold hidden sm:table-cell">Contact principal</TableHead>
                  <TableHead className="text-xs font-semibold hidden md:table-cell">Email</TableHead>
                  <TableHead className="text-xs font-semibold hidden lg:table-cell">Ville</TableHead>
                  <TableHead className="text-xs font-semibold text-center">Contacts</TableHead>
                  <TableHead className="text-xs font-semibold w-24"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map(c => {
                  const contacts = c.contacts || [];
                  const primaryContact = contacts.find(ct => ct.is_primary) || contacts[0];
                  const isExpanded = expandedClient === c.client_id;
                  return (
                    <React.Fragment key={c.client_id}>
                      <TableRow data-testid={`client-row-${c.client_id}`}>
                        <TableCell>
                          <div>
                            <p className="text-sm font-semibold text-slate-900">{c.company_name}</p>
                            <p className="text-xs text-slate-400 sm:hidden">{c.contact_name}</p>
                          </div>
                        </TableCell>
                        <TableCell className="hidden sm:table-cell">
                          <p className="text-sm text-slate-700">{primaryContact?.name || c.contact_name}</p>
                        </TableCell>
                        <TableCell className="text-sm text-slate-500 hidden md:table-cell">{primaryContact?.email || c.email}</TableCell>
                        <TableCell className="text-sm text-slate-500 hidden lg:table-cell">{c.city || "-"}</TableCell>
                        <TableCell className="text-center">
                          <Button
                            variant="ghost" size="sm"
                            className="gap-1 text-xs"
                            onClick={() => setExpandedClient(isExpanded ? null : c.client_id)}
                            data-testid={`expand-contacts-${c.client_id}`}
                          >
                            <Users className="w-3.5 h-3.5" />
                            <Badge className="bg-slate-100 text-slate-600 text-[10px] border-0 px-1.5">{contacts.length}</Badge>
                            {isExpanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                          </Button>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1">
                            <Button variant="ghost" size="sm" onClick={() => openEdit(c)} className="h-7 w-7 p-0" data-testid={`edit-client-${c.client_id}`}>
                              <Edit className="w-3.5 h-3.5" />
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => handleDelete(c.client_id)} className="h-7 w-7 p-0 text-red-400 hover:text-red-600" data-testid={`delete-client-${c.client_id}`}>
                              <Trash2 className="w-3.5 h-3.5" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                      {/* Expanded contacts */}
                      {isExpanded && (
                        <TableRow>
                          <TableCell colSpan={6} className="bg-slate-50 p-3">
                            <div className="flex items-center justify-between mb-2">
                              <h4 className="text-xs font-semibold text-slate-700">Contacts de {c.company_name}</h4>
                              <Button variant="outline" size="sm" onClick={() => openAddContact(c.client_id)} className="h-7 text-xs gap-1" data-testid={`add-contact-${c.client_id}`}>
                                <UserPlus className="w-3 h-3" /> Ajouter
                              </Button>
                            </div>
                            {contacts.length === 0 ? (
                              <p className="text-xs text-slate-400 text-center py-3">Aucun contact enregistré</p>
                            ) : (
                              <div className="space-y-1.5">
                                {contacts.map(ct => (
                                  <div key={ct.contact_id} className="flex items-center justify-between bg-white rounded-md px-3 py-2 border border-slate-200">
                                    <div className="flex items-center gap-3 min-w-0">
                                      {ct.is_primary && <Star className="w-3.5 h-3.5 text-amber-500 fill-amber-500 shrink-0" />}
                                      <div className="min-w-0">
                                        <p className="text-sm font-medium text-slate-800 truncate">{ct.name}</p>
                                        <div className="flex items-center gap-2 text-xs text-slate-500 flex-wrap">
                                          {ct.email && <span className="flex items-center gap-0.5"><Mail className="w-3 h-3" /> {ct.email}</span>}
                                          {ct.phone && <span className="flex items-center gap-0.5"><Phone className="w-3 h-3" /> {ct.phone}</span>}
                                          {ct.position && <Badge className="bg-blue-50 text-blue-600 text-[9px] border-0">{ct.position}</Badge>}
                                        </div>
                                      </div>
                                    </div>
                                    <Button variant="ghost" size="sm" onClick={() => handleDeleteContact(c.client_id, ct.contact_id)} className="h-7 w-7 p-0 text-red-400 hover:text-red-600 shrink-0" data-testid={`delete-contact-${ct.contact_id}`}>
                                      <Trash2 className="w-3 h-3" />
                                    </Button>
                                  </div>
                                ))}
                              </div>
                            )}
                          </TableCell>
                        </TableRow>
                      )}
                    </React.Fragment>
                  );
                })}
                {filtered.length === 0 && (
                  <TableRow><TableCell colSpan={6} className="text-center py-12 text-slate-400">Aucun client trouvé</TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Client Form Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-[95vw] sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-barlow text-lg uppercase tracking-tight">{editClient ? "Modifier le client" : "Nouveau Client"}</DialogTitle>
            <DialogDescription>Informations de l'entreprise</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-1"><Label className="text-xs">Société *</Label><Input value={form.company_name} onChange={e => setForm(f => ({ ...f, company_name: e.target.value }))} data-testid="form-company" /></div>
              <div className="space-y-1"><Label className="text-xs">Contact principal</Label><Input value={form.contact_name} onChange={e => setForm(f => ({ ...f, contact_name: e.target.value }))} data-testid="form-contact" /></div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-1"><Label className="text-xs">Email</Label><Input type="email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} data-testid="form-email" /></div>
              <div className="space-y-1"><Label className="text-xs">Téléphone</Label><Input value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} data-testid="form-phone" /></div>
            </div>
            <div className="space-y-1"><Label className="text-xs">Adresse</Label><Input value={form.address} onChange={e => setForm(f => ({ ...f, address: e.target.value }))} data-testid="form-address" /></div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              <div className="space-y-1"><Label className="text-xs">Code postal</Label><Input value={form.postal_code} onChange={e => setForm(f => ({ ...f, postal_code: e.target.value }))} data-testid="form-postal" /></div>
              <div className="space-y-1"><Label className="text-xs">Ville</Label><Input value={form.city} onChange={e => setForm(f => ({ ...f, city: e.target.value }))} data-testid="form-city" /></div>
              <div className="space-y-1 col-span-2 sm:col-span-1"><Label className="text-xs">SIRET</Label><Input value={form.siret} onChange={e => setForm(f => ({ ...f, siret: e.target.value }))} data-testid="form-siret" /></div>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setDialogOpen(false)}>Annuler</Button>
              <Button onClick={handleSave} className="bg-slate-900 hover:bg-slate-800" data-testid="save-client-btn">{editClient ? "Mettre à jour" : "Créer"}</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add Contact Dialog */}
      <Dialog open={contactDialog} onOpenChange={setContactDialog}>
        <DialogContent className="max-w-[95vw] sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-barlow text-lg uppercase tracking-tight flex items-center gap-2"><UserPlus className="w-5 h-5" /> Nouveau Contact</DialogTitle>
            <DialogDescription>Ajouter un contact pour cette entreprise</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1"><Label className="text-xs">Nom *</Label><Input value={contactForm.name} onChange={e => setContactForm(f => ({ ...f, name: e.target.value }))} data-testid="contact-name" /></div>
            <div className="space-y-1"><Label className="text-xs">Email *</Label><Input type="email" value={contactForm.email} onChange={e => setContactForm(f => ({ ...f, email: e.target.value }))} data-testid="contact-email" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1"><Label className="text-xs">Téléphone</Label><Input value={contactForm.phone} onChange={e => setContactForm(f => ({ ...f, phone: e.target.value }))} data-testid="contact-phone" /></div>
              <div className="space-y-1"><Label className="text-xs">Poste</Label><Input placeholder="Ex: Directeur" value={contactForm.position} onChange={e => setContactForm(f => ({ ...f, position: e.target.value }))} data-testid="contact-position" /></div>
            </div>
            <div className="flex items-center justify-between p-3 bg-amber-50 rounded-md">
              <Label className="text-xs text-amber-800">Contact principal</Label>
              <Switch checked={contactForm.is_primary} onCheckedChange={v => setContactForm(f => ({ ...f, is_primary: v }))} data-testid="contact-primary-toggle" />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setContactDialog(false)}>Annuler</Button>
              <Button onClick={handleAddContact} className="bg-slate-900 hover:bg-slate-800 gap-2" data-testid="save-contact-btn"><UserPlus className="w-4 h-4" /> Ajouter</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Need React for Fragment
import React from "react";
